import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ApiError } from '../lib/api';
import { grad } from '../components/ui';

export function Login() {
  const { sendOtp, isLoggedIn } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // Revealed only when the server tells us this address is new, so returning
  // users just type an email and go.
  const [needsProfile, setNeedsProfile] = useState(false);
  // Separate from `error`: this is guidance, not a failure.
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isLoggedIn) navigate('/', { replace: true });
  }, [isLoggedIn, navigate]);

  const emailOk = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(email.trim());
  const profileOk = /^\d{10}$/.test(phone) && name.trim().length >= 2;
  const valid = emailOk && (!needsProfile || profileOk);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    setError('');
    const address = email.trim().toLowerCase();
    try {
      const { demoOtp } = await sendOtp(address, needsProfile ? { name: name.trim(), phone } : undefined);
      navigate('/otp', { state: { email: address, demoOtp } });
    } catch (err) {
      // First time this address is seen — ask for the details and let them retry.
      if (err instanceof ApiError && (err.body as { needsProfile?: boolean })?.needsProfile) {
        setNeedsProfile(true);
        setError('');
        setNotice("Enter your name and number and you're in.");
      } else {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 420, padding: 0, overflow: 'hidden' }}>
        <div className="hero" style={{ ...grad(['#7C4DBE', '#5A2D9C']), borderRadius: 0, textAlign: 'center', paddingTop: 34 }}>
          <div style={{ fontSize: 44 }}>📚</div>
          <h1 style={{ fontSize: 26, fontWeight: 900, marginTop: 6 }}>PulsePrep</h1>
          <p style={{ opacity: 0.9, fontSize: 14, marginTop: 6 }}>Notes, PYQs, Quizzes & Test Series — all in one place</p>
        </div>
        <form onSubmit={submit} style={{ padding: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 900 }}>
            {needsProfile ? 'Create your account' : 'Sign in or sign up'}
          </h2>
          <p className="muted" style={{ fontSize: 14, marginTop: 4 }}>
            {needsProfile
              ? 'Just a couple of details and we will send your code.'
              : 'Enter your email and we will send a one-time code. New here? We will set you up — no password needed.'}
          </p>

          {notice && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'rgba(14,154,165,.10)',
                color: 'var(--text)',
                fontSize: 13.5,
                fontWeight: 600,
              }}>
              {notice}
            </div>
          )}

          <div className="field">
            <label className="label">Email address</label>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoCapitalize="none"
            />
          </div>

          {needsProfile && (
            <>
              <div className="field">
                <label className="label">Full name</label>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aditya Sharma" autoFocus />
              </div>
              <div className="field">
                <label className="label">Phone number</label>
                <input
                  className="input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                />
              </div>
            </>
          )}

          {error && <div className="error-text">{error}</div>}

          <button type="submit" className="btn btn-primary btn-block" style={{ marginTop: 22 }} disabled={!valid || loading}>
            {loading ? 'Sending…' : needsProfile ? 'Create account →' : 'Send code →'}
          </button>
          <p className="faint" style={{ fontSize: 12, textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
            By continuing you agree to our Terms of Service & Privacy Policy.
          </p>
        </form>
      </div>
    </div>
  );
}
